import {
    Card,
    Text,
    Box,
    BlockStack,
    InlineStack,
    Badge,
    Button,
    TextField,
    DataTable,
    EmptyState,
    ProgressBar,
    Select,
    InlineGrid,
    Divider,
    Pagination,
} from '@shopify/polaris';
import {
    PlusIcon,
    DeleteIcon,
} from '@shopify/polaris-icons';
import { CommissionOverride } from './Modals/CommissionOverride';
import apiClient from '../utils/api';
import { DEFAULT_PAGE_SIZE, PageSizeSelect } from '../utils/table';
import { useState } from 'react';

const COMMISSION_BASIS_OPTIONS = [
    { label: 'Net Sales (after refunds)', value: 'NET_SALES' },
    { label: 'Sales (before refunds)', value: 'GROSS_SALES' },
];

// Describe refund behaviour only for a basis we actually have. The previous ternary treated
// every non-GROSS_SALES value - including an unknown one - as "will be reduced on refunds",
// stating a billing rule the API had not told us.
const basisHelpText = (basis) => {
    if (basis === 'GROSS_SALES') return 'Commission will NOT be reduced on refunds';
    if (basis === 'NET_SALES') return 'Commission will be reduced on refunds';
    return 'Calculation basis has not loaded for this brand.';
};

export const CommissionSettings = ({
    saving,
    setSaving,
    isSyncing,
    defaultRate,
    setDefaultRate,
    updateDefaultCommission,
    brand,
    availableMarketplaceTags,
    availableMarketplacesLoading,
    setError,
    fetchBrandData,
}) => {
    const [showCommissionModal, setShowCommissionModal] = useState(false);

    // Overrides paging. The whole list arrives with the brand record, so the page is cut from
    // the array in hand and a size change costs no request.
    const [overridesPage, setOverridesPage] = useState(1);
    const [overridesPageSize, setOverridesPageSize] = useState(DEFAULT_PAGE_SIZE);

    // Split commission state.
    //
    // These four fields are billing inputs and Save writes whatever is in the box straight to
    // the brand record, so nothing here may invent a value. All four columns are NON-NULLABLE
    // with server-side defaults (schema.prisma:62-65: defaultMarketplaceCommissionRate 0,
    // default2wCommissionRate 0.30, both bases NET_SALES), which means a loaded brand always
    // carries a real figure - there is nothing legitimate for the client to substitute. When
    // the value is genuinely absent (brand not loaded / partial payload) the field stays EMPTY
    // and Save is refused, instead of showing a plausible 30.0% that a stray click would
    // persist as though the brand had agreed to it.
    const asPercent = (rate) =>
        typeof rate === 'number' && Number.isFinite(rate) ? (rate * 100).toFixed(1) : '';

    const brandMarketplaceRate = asPercent(brand?.defaultMarketplaceCommissionRate);
    // No fallback to defaultCommissionRate, for the reason given above and one more: that
    // column is the COMBINED rate, marketplace plus 2W. Substituting it here offered the sum
    // of both legs as the 2W rate, in a form whose Save writes the box straight to the brand.
    const brandTwRate = asPercent(brand?.default2wCommissionRate);
    const brandMarketplaceBasis = brand?.marketplaceCommissionBasis || '';
    const brandTwBasis = brand?.twCommissionBasis || '';

    const brandFingerprint = `${brand?.id}|${brandMarketplaceRate}|${brandTwRate}|${brandMarketplaceBasis}|${brandTwBasis}`;

    const [marketplaceRate, setMarketplaceRate] = useState(brandMarketplaceRate);
    const [twRate, setTwRate] = useState(brandTwRate);
    const [marketplaceBasis, setMarketplaceBasis] = useState(brandMarketplaceBasis);
    const [twBasis, setTwBasis] = useState(brandTwBasis);

    // Re-sync when the brand record itself changes. The lazy useState initialisers above only
    // ran once, so a component mounted before the brand finished loading (or re-fetched after a
    // save elsewhere) kept displaying the first snapshot forever.
    const [syncedFrom, setSyncedFrom] = useState(brandFingerprint);
    if (syncedFrom !== brandFingerprint) {
        setSyncedFrom(brandFingerprint);
        setMarketplaceRate(brandMarketplaceRate);
        setTwRate(brandTwRate);
        setMarketplaceBasis(brandMarketplaceBasis);
        setTwBasis(brandTwBasis);
    }

    /**
     * Whether this form differs from the brand it was loaded from.
     *
     * Rates compare as NUMBERS. The fields hold text and the brand's value is rendered to one
     * decimal, so "30" and "30.0" are the same rate typed two ways - a string comparison would
     * call that an edit and offer to save a value the brand already has. An empty field is not
     * a number and is only equal to another empty field; it means the brand had no rate to
     * show, and Save refuses it further down anyway.
     */
    const sameRate = (a, b) => {
        const l = parseFloat(a);
        const r = parseFloat(b);
        if (Number.isNaN(l) || Number.isNaN(r)) return String(a) === String(b);
        return l === r;
    };

    const dirty = !sameRate(marketplaceRate, brandMarketplaceRate)
        || !sameRate(twRate, brandTwRate)
        || marketplaceBasis !== brandMarketplaceBasis
        || twBasis !== brandTwBasis;

    const updateCommissionSettings = async () => {
        const mktValue = parseFloat(marketplaceRate);
        const twValue = parseFloat(twRate);

        if (isNaN(mktValue) || mktValue < 0 || mktValue > 100) {
            setError('Marketplace Commission Rate must be between 0 and 100');
            return;
        }
        if (isNaN(twValue) || twValue < 0 || twValue > 100) {
            setError('2W Commission Rate must be between 0 and 100');
            return;
        }
        // Refuse to persist a basis the API never sent rather than defaulting it client-side.
        if (!marketplaceBasis || !twBasis) {
            setError('Commission calculation basis is not loaded yet. Reload the brand before saving.');
            return;
        }

        try {
            setSaving(true);
            await apiClient.updateBrand(brand.id, {
                defaultMarketplaceCommissionRate: mktValue / 100,
                default2wCommissionRate: twValue / 100,
                marketplaceCommissionBasis: marketplaceBasis,
                twCommissionBasis: twBasis,
            });
            await fetchBrandData();
        } catch (err) {
            setError(err.message || 'Failed to update commission settings');
        } finally {
            setSaving(false);
        }
    };

    const deleteCommissionOverride = async (overrideId) => {
        if (!confirm('Are you sure you want to delete this commission override?')) return;

        try {
            setSaving(true);
            await apiClient.deleteCommissionOverride(brand.id, overrideId);
            await fetchBrandData();
        } catch (err) {
            setError(err.message || 'Failed to delete commission override');
        } finally {
            setSaving(false);
        }
    };

    /**
     * The page of overrides on screen.
     *
     * CLAMPED, because the list shrinks: deleting an override re-fetches the brand, so the
     * last row of the last page can disappear under the page it was on, and an out-of-range
     * slice would render an empty table beside a pager claiming a page 3.
     */
    const allOverrides = brand.commissionOverrides || [];
    const overridesTotalPages = Math.max(1, Math.ceil(allOverrides.length / overridesPageSize));
    const overridesSafePage = Math.min(overridesPage, overridesTotalPages);
    const visibleOverrides = allOverrides.slice(
        (overridesSafePage - 1) * overridesPageSize,
        overridesSafePage * overridesPageSize,
    );

    // A new row count starts again at page 1, so picking 50 on page 4 does not land past the end.
    const changeOverridesPageSize = (next) => {
        setOverridesPageSize(next);
        setOverridesPage(1);
    };

    return (
        <>
            <BlockStack gap="600">
                {/* Commission Rates */}
                <Card>
                    <Box padding="200">
                        <BlockStack gap="500">
                            {/* Save lives in the card header, per the wireframe — with the rates
                                and the overrides table both on this card, a button at the very
                                bottom read as belonging to the table rather than the rates. */}
                            <InlineStack align="space-between" blockAlign="center" gap="400">
                                <BlockStack gap="200" align="start">
                                    <Text variant="headingMd" as="h3">Commission Rates</Text>
                                    <Text variant="bodyMd" tone="subdued">
                                        Set default rates for both commission types. Each type can use a different calculation basis.
                                    </Text>
                                    {/* Says the one thing the basis labels cannot. Both options are
                                        about REFUNDS — whether a refund claws commission back —
                                        while discounts are deducted from the base either way
                                        (unified-sync-service.js:1133 multiplies netAmount, which is
                                        already net of discounts). Without this, an admin reading
                                        "Sales (before refunds)" reasonably assumes a discounted
                                        order is charged on its full ticket price. */}
                                    <Text variant="bodySm" tone="subdued">
                                        Both bases are charged on sales <b>after discounts</b>. The choice
                                        only controls what happens on a refund.
                                    </Text>
                                </BlockStack>
                                <Button
                                    variant="primary"
                                    onClick={updateCommissionSettings}
                                    loading={saving}
                                    disabled={saving || isSyncing || !dirty}
                                >
                                    Save
                                </Button>
                            </InlineStack>

                            <InlineGrid columns={{ xs: 1, md: 2 }} gap="600">
                                {/* Bare labelled groups, not nested Cards. The wireframe has no
                                    inner borders here, and a Card inside a Card reads as a second
                                    unrelated panel rather than as two halves of one setting.
                                    Rate and Basis sit side by side so each commission type is one
                                    scannable row instead of a four-field stack. */}
                                <BlockStack gap="300">
                                    <Text variant="headingSm" as="h4">Marketplace Commission</Text>
                                    <Text variant="bodySm" tone="subdued">
                                        External marketplace fees charged per order
                                    </Text>
                                    <InlineGrid columns={{ xs: 1, sm: 2 }} gap="300">
                                        <TextField
                                            label="Rate"
                                            type="number"
                                            value={marketplaceRate}
                                            onChange={setMarketplaceRate}
                                            suffix="%"
                                            min="0"
                                            max="100"
                                            step="0.1"
                                            autoComplete="off"
                                        />
                                        <Select
                                            label="Basis"
                                            options={COMMISSION_BASIS_OPTIONS}
                                            value={marketplaceBasis}
                                            onChange={setMarketplaceBasis}
                                            placeholder={marketplaceBasis ? undefined : 'Not loaded'}
                                            helpText={basisHelpText(marketplaceBasis)}
                                        />
                                    </InlineGrid>
                                </BlockStack>

                                <BlockStack gap="300">
                                    <Text variant="headingSm" as="h4">2W Commission</Text>
                                    <Text variant="bodySm" tone="subdued">
                                        2W service fee charged per order
                                    </Text>
                                    <InlineGrid columns={{ xs: 1, sm: 2 }} gap="300">
                                        <TextField
                                            label="Rate"
                                            type="number"
                                            value={twRate}
                                            onChange={setTwRate}
                                            suffix="%"
                                            min="0"
                                            max="100"
                                            step="0.1"
                                            autoComplete="off"
                                        />
                                        <Select
                                            label="Basis"
                                            options={COMMISSION_BASIS_OPTIONS}
                                            value={twBasis}
                                            onChange={setTwBasis}
                                            placeholder={twBasis ? undefined : 'Not loaded'}
                                            helpText={basisHelpText(twBasis)}
                                        />
                                    </InlineGrid>
                                </BlockStack>
                            </InlineGrid>

                            {saving && <ProgressBar progress={undefined} size="small" />}

                            {/* One card, two sections, divided — not two cards.

                                An override only means something relative to the default it
                                overrides: "Overrides take priority over defaults" is a sentence
                                about the fields directly above it. Two cards framed them as two
                                unrelated settings, and the card boundary carried no information
                                the heading does not already carry. A divider separates them at
                                less weight and keeps the relationship on screen.

                                Save stays in the Commission Rates header. That placement was
                                chosen for exactly this arrangement — at the foot of a card that
                                ends in the overrides table it reads as saving the table, which it
                                does not: an override is written immediately from its modal and is
                                not gated by Save. */}
                            <Divider />

                            <InlineStack align="space-between" blockAlign="center">
                                <BlockStack gap="200">
                                    {/* "Marketplace Overrides", not "Retailer-Specific".

                                        The general rule in this UI is "retailers", not
                                        "marketplaces" — but commission naming is the standing
                                        exception ("Marketplace Commission", "Marketplace Rate"),
                                        and this section overrides exactly that. It now agrees
                                        with the fields above it, the "Marketplace Commission %"
                                        column below it, and its own description, which already
                                        said "specific marketplaces". */}
                                    <Text variant="headingMd" as="h3">Marketplace Overrides</Text>
                                    <Text variant="bodyMd" tone="subdued">
                                        Set different commission rates for specific marketplaces. Overrides take priority over defaults.
                                    </Text>
                                </BlockStack>
                                <Button
                                    variant="primary"
                                    icon={PlusIcon}
                                    onClick={() => setShowCommissionModal(true)}
                                >
                                    Add
                                </Button>
                            </InlineStack>

                            {/* THE TABLE IS RENDERED WHETHER OR NOT IT HAS ROWS.

                                An empty override list used to REPLACE the table with an
                                EmptyState, so a brand with no overrides saw no column names
                                and no hint of what an override even consists of — the defect
                                utils/table.jsx fixed for every IndexTable in this console.
                                DataTable has no `itemCount` or `emptyState` to lean on, but it
                                derives a cell's colSpan from the row's length against
                                headings.length (DataTable.js getColSpan), so a row holding ONE
                                cell spans the whole table. That is the empty state now: the
                                headings stay put and the message sits under them.

                                All 'text', so every column is left-aligned.

                                The two rate columns were 'numeric', which right-aligns the
                                header AND the cell — so on a wide card each figure sat at the
                                far edge of its column, a long way from the marketplace it
                                belongs to, and the row read as unrelated values rather than one
                                override. Right alignment earns its keep when figures are
                                compared down a long column of varying magnitudes; here they are
                                one-decimal percentages over a handful of rows, so reading
                                across matters more than lining up the decimal point.

                                Headings are Text nodes rather than bare strings: DataTable
                                renders a plain string at body weight, so the header row read as
                                just another row of values. semibold is the weight Polaris gives
                                IndexTable headers, so this table now matches the ones on the
                                Connector screens. */}
                            <BlockStack gap="300">
                                <DataTable
                                    columnContentTypes={['text', 'text', 'text', 'text', 'text']}
                                    headings={[
                                        'Marketplace',
                                        'Marketplace Commission %',
                                        '2W Commission %',
                                        'Created',
                                        'Actions',
                                    ].map((h) => (
                                        <Text key={h} variant="bodyMd" fontWeight="semibold" as="span">{h}</Text>
                                    ))}
                                    rows={allOverrides.length === 0 ? [[
                                        // No action on the empty state: the section header
                                        // already carries Add, a few inches above. Two
                                        // identical buttons in one eyeline make the reader
                                        // stop to work out whether they differ.
                                        <EmptyState
                                            key="no-overrides"
                                            heading="No commission overrides configured"
                                            content="Add marketplace-specific commission rates to override the default rates."
                                        />,
                                    ]] : visibleOverrides.map(override => [
                                        <Badge tone="info">{override.orderTag}</Badge>,
                                        `${((override.marketplaceCommissionRate || 0) * 100).toFixed(1)}%`,
                                        // No fall back to the override's own commissionRate: that is the COMBINED rate, so
                                        // this column showed both legs as the 2W rate.
                                        `${((override.twCommissionRate || 0) * 100).toFixed(1)}%`,
                                        (() => {
                                            const date = new Date(override.createdAt || Date.now());
                                            return isNaN(date.getTime()) ? 'Unknown' : date.toLocaleDateString();
                                        })(),
                                        <Button
                                            variant="tertiary"
                                            tone="critical"
                                            size="micro"
                                            icon={DeleteIcon}
                                            onClick={() => deleteCommissionOverride(override.id)}
                                            loading={saving}
                                        />
                                    ])}
                                />
                                {/* Both controls in one row. DataTable has no footer slot, so
                                    they sit under it in the same stack. The pager drops out
                                    when every override fits; the row count stays, because it
                                    is how someone gets a second page at all. Neither is shown
                                    over an empty table: there is no page to size. */}
                                {allOverrides.length > 0 && (
                                    <InlineStack align="space-between" blockAlign="center">
                                        <PageSizeSelect
                                            value={overridesPageSize}
                                            onChange={changeOverridesPageSize}
                                        />
                                        {overridesTotalPages > 1 && (
                                            <Pagination
                                                hasPrevious={overridesSafePage > 1}
                                                onPrevious={() => setOverridesPage(overridesSafePage - 1)}
                                                hasNext={overridesSafePage < overridesTotalPages}
                                                onNext={() => setOverridesPage(overridesSafePage + 1)}
                                                label={`Page ${overridesSafePage} of ${overridesTotalPages}`}
                                            />
                                        )}
                                    </InlineStack>
                                )}
                            </BlockStack>
                        </BlockStack>
                    </Box>
                </Card>
            </BlockStack>
            <CommissionOverride
                brand={brand}
                availableMarketplaceTags={availableMarketplaceTags}
                availableMarketplacesLoading={availableMarketplacesLoading}
                saving={saving}
                showCommissionModal={showCommissionModal}
                setShowCommissionModal={setShowCommissionModal}
                setSaving={setSaving}
                setError={setError}
                fetchBrandData={fetchBrandData}
            />
        </>
    )
}
